import React, { useMemo, useState } from 'react';
import {
	RECITALS,
	CLOSURES,
	FLEX_DAYS,
	FIRST_LESSON_BY_WEEKDAY,
	closureInfoFor,
} from '../../data/seasonData';
import './SeasonMonthGrid.css';

// A season overview month grid for the Season Calendar page. Canon-driven and
// read-only: it marks studio closures (muted) and recital days (gold) — never
// personal lessons, so it needs no auth-scoped reads.
//
// Flex/reserve days are the one place staff and families diverge. To a family a
// flex day IS closed — no lesson happens — so the parent-facing route renders it
// as a plain closed day. `showFlex` (the staff Important-Dates tab) instead
// marks it "Reserve", because staff can still place a makeup there. Both come
// from the same closureInfoFor entry; only the label differs.

const MON = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

// Every dated thing in the canon → the [first, last] month span to page across.
const seasonMonthSpan = () => {
	const dates = [];
	Object.values(FIRST_LESSON_BY_WEEKDAY).forEach((d) => d && dates.push(d));
	RECITALS.forEach((r) => r.date && dates.push(r.date));
	CLOSURES.forEach((c) => { dates.push(c.start); dates.push(c.end); });
	FLEX_DAYS.forEach((f) => { dates.push(f.start); dates.push(f.end); });
	dates.sort();
	const parse = (s) => { const [y, m] = s.split('-').map(Number); return { y, m: m - 1 }; };
	const start = parse(dates[0]);
	const end = parse(dates[dates.length - 1]);
	const months = [];
	for (let y = start.y, m = start.m; y < end.y || (y === end.y && m <= end.m);) {
		months.push({ y, m });
		m += 1;
		if (m > 11) { m = 0; y += 1; }
	}
	return months;
};

const SeasonMonthGrid = ({ showFlex = false }) => {
	const months = useMemo(seasonMonthSpan, []);
	const now = new Date();
	const todayStr = ymd(now.getFullYear(), now.getMonth(), now.getDate());

	const startIdx = useMemo(() => {
		const i = months.findIndex((x) => x.y === now.getFullYear() && x.m === now.getMonth());
		return i >= 0 ? i : 0;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [months]);
	const [idx, setIdx] = useState(startIdx);

	if (!months.length) return null;
	const { y, m } = months[idx];
	const startDow = new Date(y, m, 1).getDay();
	const daysInMonth = new Date(y, m + 1, 0).getDate();

	const cells = [];
	for (let i = 0; i < startDow; i += 1) cells.push(null);
	for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

	return (
		<div className='smg'>
			<div className='smg__head'>
				<button type='button' className='smg__nav' disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} aria-label='Previous month'>
					<i className='fa-sharp fa-solid fa-chevron-left' />
				</button>
				<div className='smg__title'>{MON[m]} {y}</div>
				<button type='button' className='smg__nav' disabled={idx === months.length - 1} onClick={() => setIdx((i) => Math.min(months.length - 1, i + 1))} aria-label='Next month'>
					<i className='fa-sharp fa-solid fa-chevron-right' />
				</button>
			</div>

			<div className='smg__grid'>
				{DOW.map((d) => <div key={d} className='smg__dow'>{d}</div>)}
				{cells.map((d, i) => {
					if (d === null) return <div key={`b${i}`} className='smg__cell smg__cell--empty' />;
					const ds = ymd(y, m, d);
					const info = closureInfoFor(ds);
					const recital = info && info.type === 'recital';
					// A flex day is closed to families and reserve to staff. Same
					// canon entry, two readings: only `showFlex` (staff) gets the
					// distinct treatment — a family sees a plain closed day, which
					// is what it is to them.
					const flex = showFlex && info && info.type === 'flex';
					const cls = ['smg__cell'];
					if (recital) cls.push('smg__cell--recital');
					else if (flex) cls.push('smg__cell--flex');
					else if (info) cls.push('smg__cell--closed');
					if (ds === todayStr) cls.push('smg__cell--today');
					const title = flex
						? 'Reserve day — makeups only, no regular lessons'
						: (info ? info.name + (info.location ? ` · ${info.location}` : '') : '');
					return (
						<div key={ds} className={cls.join(' ')} title={title}>
							<span className='smg__num'>{d}</span>
							{flex && <span className='smg__tag smg__tag--flex'>Reserve</span>}
							{!flex && info && (
								<span className='smg__tag'>
									{recital && <i className='fa-sharp fa-solid fa-star' />}
									{recital ? 'Recital' : 'Closed'}
								</span>
							)}
						</div>
					);
				})}
			</div>

			<div className='smg__legend'>
				<span className='smg__leg'><span className='smg__sw smg__sw--recital' />Recital</span>
				<span className='smg__leg'><span className='smg__sw smg__sw--closed' />Studio closed</span>
				{showFlex && <span className='smg__leg'><span className='smg__sw smg__sw--flex' />Reserve (makeups only)</span>}
				<span className='smg__leg'><span className='smg__sw smg__sw--today' />Today</span>
			</div>
		</div>
	);
};

export default SeasonMonthGrid;
